import { useSyncExternalStore, useCallback, useEffect, useRef } from 'react';

// https://usehooks.com/useLocalStorage/
function dispatchStorageEvent(key, newValue) {
  window.dispatchEvent(new StorageEvent('storage', { key, newValue }));
}

const setLocalStorageItem = (key, value) => {
  const stringifiedValue = JSON.stringify(value);
  window.localStorage.setItem(key, stringifiedValue);
  dispatchStorageEvent(key, stringifiedValue);
  return stringifiedValue;
};

const removeLocalStorageItem = key => {
  window.localStorage.removeItem(key);
  dispatchStorageEvent(key, null);
};

const getLocalStorageItem = key => {
  return window.localStorage.getItem(key);
};

const parseLocalStorageItem = (value, fallback) => {
  if (value === null) return fallback;

  try {
    return JSON.parse(value);
  } catch {
    return fallback;
  }
};

const useLocalStorageSubscribe = callback => {
  window.addEventListener('storage', callback);
  return () => window.removeEventListener('storage', callback);
};

const getLocalStorageServerSnapshot = () => {
  throw Error('useLocalStorage is a client-only hook');
};

export function useLocalStorage(key, initialValue) {
  const getSnapshot = () => getLocalStorageItem(key);

  const store = useSyncExternalStore(useLocalStorageSubscribe, getSnapshot, getLocalStorageServerSnapshot);
  const storeRef = useRef(store);
  const initialValueRef = useRef(initialValue);
  storeRef.current = store;

  const setState = useCallback(
    v => {
      try {
        const nextState = typeof v === 'function' ? v(parseLocalStorageItem(storeRef.current, initialValueRef.current)) : v;

        if (nextState === undefined || nextState === null) {
          removeLocalStorageItem(key);
          storeRef.current = null;
        } else {
          storeRef.current = setLocalStorageItem(key, nextState);
        }
      } catch (e) {
        console.warn(e);
      }
    },
    [key],
  );

  useEffect(() => {
    if (getLocalStorageItem(key) === null && initialValueRef.current !== undefined) {
      setLocalStorageItem(key, initialValueRef.current);
    }
  }, [key]);

  return [parseLocalStorageItem(store, initialValueRef.current), setState];
}
